import React, { memo, useCallback, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import { Button, Collapse, Typography } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { NAVIGATION_ITEM_TYPE } from 'constants/navigation';

import NavigationMenuGroupItems from '../NavigationMenuGroupItems';

import { getButtonStyles } from './NavigationMenuGroupItem.styles';
import { NavigationMenuGroupItemTestIds } from './NavigationMenuGroupItem.testIds';

const checkIsUrlInChildren = (parent, url) => {
  if (!parent.children) {
    return false;
  }

  return parent.children.some(child => {
    if (child.children && checkIsUrlInChildren(child, url)) {
      return true;
    }

    return !!matchPath(url, { path: child.url });
  });
};

const NavigationMenuItemGroupItem = ({
  isVertical = false,
  onClose,
  onCloseSidebar,
  isChildrenExpanded = false,
  ...item
}) => {
  const location = useLocation();

  const isUrlInChildren = useMemo(
    () => location && checkIsUrlInChildren(item, location.pathname),
    [item, location],
  );

  const [isLocallyExpanded, setIsLocallyExpanded] = useState(isUrlInChildren);

  const { type, icon: StartIcon, title, url } = item;
  const isCollapseTypeItem = type === NAVIGATION_ITEM_TYPE.COLLAPSE;
  const isExpanded = isChildrenExpanded || isLocallyExpanded;
  const isCurrentUrl = !!matchPath(location.pathname, { path: url, end: false });

  const onToggleIsExpanded = useCallback(() => setIsLocallyExpanded(prev => !prev), []);

  const onItemClick = useCallback(
    e => {
      onClose(e);

      if (onCloseSidebar) {
        onCloseSidebar();
      }
    },
    [onClose, onCloseSidebar],
  );

  return (
    <>
      <Button
        className="truncate"
        component={!isCollapseTypeItem ? Link : undefined}
        data-is-active={isCurrentUrl}
        data-testid={
          isCollapseTypeItem
            ? NavigationMenuGroupItemTestIds.getSubmenuId(title)
            : NavigationMenuGroupItemTestIds.getLinkId(title)
        }
        endIcon={
          isCollapseTypeItem ? (
            <ArrowCheckIcon
              className="self-end"
              direction={isExpanded ? 'up' : 'down'}
              size={IconSize[16]}
            />
          ) : undefined
        }
        startIcon={StartIcon && <StartIcon />}
        sx={getButtonStyles({ isVertical })}
        tabIndex={0}
        to={!isCollapseTypeItem ? url : undefined}
        onClick={isCollapseTypeItem ? onToggleIsExpanded : onItemClick}
      >
        <Typography data-truncated className="truncate" variant="regular16">
          {title}
        </Typography>
      </Button>

      {isCollapseTypeItem && (
        <Collapse className="w-full" in={isExpanded} timeout={100}>
          <NavigationMenuGroupItems
            isNestedList
            isChildrenExpanded={isChildrenExpanded}
            isVertical={isVertical}
            onClose={onClose}
            onCloseSidebar={onCloseSidebar}
            {...item}
          />
        </Collapse>
      )}
    </>
  );
};

NavigationMenuItemGroupItem.propTypes = {
  onClose: PropTypes.func.isRequired,
  isNestedList: PropTypes.bool,
  isChildrenExpanded: PropTypes.bool,
  isVertical: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default memo(NavigationMenuItemGroupItem);
